<script setup>
import ShowOptions from '@components/show/show-options'

defineProps({
    show: {
        type: Object,
        default: () => {},
    },
    extended: {
        // Centered on mobiles, expanded width on desktops
        type: Boolean,
        default: false,
    },
    hasTypeTitle: {
        type: Boolean,
        default: false,
    },
})
</script>

<template>
    <div v-if="show">
        <div :class="['show-large-tile', extended && 'show-large-tile-extended']">
            <div class="image-wrap">
                <div class="image-container">
                    <BaseLink :to="{ name: 'show', params: { id: show.id } }">
                        <BaseResourceImage
                            class="image"
                            :resource="show || false"
                        />
                    </BaseLink>
                </div>
                <div
                    class="options"
                    @click.prevent="() => {}"
                >
                    <BaseDropdown :padded="false">
                        <template #trigger>
                            <BaseButton
                                :modifiers="{ autoWidth: true, transparent: true, dots: true }"
                                button-type="tag"
                                aria-label="Options"
                                size="small"
                            />
                        </template>
                        <template #dropdown>
                            <div>
                                <ShowOptions :show="show" />
                            </div>
                        </template>
                    </BaseDropdown>
                </div>
            </div>
            <div class="main">
                <BaseLink :to="{ name: 'show', params: { id: show.id } }">
                    <div class="title">
                        {{ show.title }}
                    </div>
                </BaseLink>
                <div
                    v-if="show.artist"
                    class="subtitle"
                >
                    <BaseLink :to="{ path: `/artist/${show.artist.url}/${show.artist.id}` }">
                        {{ show.artist.title }}
                    </BaseLink>
                </div>
            </div>
            <div
                v-if="hasTypeTitle"
                class="type-title"
            >Show</div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.options {
    z-index: $layer-dropdown-z-index;
    opacity: 0;

    @include transition(opacity, slow);
    @include center;
}

.main {
    flex-basis: 100%;

    @include tablet {
        flex-basis: span(3 of 12);
    }
}

.show-large-tile {
    display: flex;
    flex-wrap: wrap;

    &-extended {
        text-align: center;

        .main {
            margin-top: $s-xl;
        }

        @include tablet {
            display: grid;
            grid-template-areas: 'image main' 'typeTitle null';
            grid-template-columns: 264px max-content;
            gap: $s-xl;
            align-items: center;

            .main {
                margin-top: unset;
                text-align: left;
            }

            .image-wrap {
                max-width: unset;
            }
        }
    }
}

.image-wrap {
    position: relative;
    flex-basis: 100%;
    grid-area: image;

    @include tablet {
        flex-basis: span(2 of 12 narrow);
        max-width: 200px;
    }

    &::after {
        @include fill;

        content: '';
        background: $c-surface-raised;
        border-radius: $size-radius-artwork;
        opacity: 0;

        @include transition(opacity, slow);
    }

    &:hover {
        .options {
            opacity: 1;

            @include transition(opacity, base);
        }

        &::after {
            opacity: 0.5;

            @include transition(opacity, base);
        }
    }
}

.image-container {
    position: relative;
    height: 200px;
    margin-bottom: auto;
    overflow: hidden;
    border-radius: $size-radius-artwork;
}

.image {
    @include center;

    width: 100%;
}

.title {
    @extend %contentTitleL;

    margin-top: $s-xxs;
    margin-bottom: $s-s;
}

.subtitle {
    @extend %contentSubtitleM;

    margin-bottom: $s-m;

    a {
        color: $c-text-secondary;
    }
}

.type-title {
    @extend %contentTitleM;

    margin: auto 0;
    text-align: center;
}

// The show-title link wraps a block div; pin the wrapper to the text colour so
// the hover underline matches it (FDS Typography 04).
.main > a {
    color: $c-text-primary;
}
</style>
